import { Divider, ListSubheader, Menu, MenuItem } from '@mui/material'
import { useTranslation } from 'react-i18next'
import {
  accountMenuCurrentRestaurantStyles,
  accountMenuDividerStyles,
  accountMenuItemStyles,
  accountMenuLogoutStyles,
  accountMenuPaperStyles
} from './AccountMenu.styles'

interface AccountMenuProps {
  id: string
  anchorEl: HTMLElement | null
  /** The open venue, unknown until the venue list has loaded. */
  restaurantName?: string
  onClose: () => void
  onSettings: () => void
  onHelp: () => void
  onTour: () => void
  onLogout: () => void
}

interface AccountMenuItemProps {
  icon: string
  label: string
  onClick: () => void
  sx?: typeof accountMenuItemStyles
}

// Icons at `icon-big`, the size the sidebar's own entries use.
function AccountMenuItem(props: AccountMenuItemProps) {
  const { icon, label, onClick, sx = accountMenuItemStyles } = props
  return (
    <MenuItem onClick={onClick} sx={sx} disableRipple>
      <i className={`${icon} icon-big`} />
      <span className="body-bold-medium">{label}</span>
    </MenuItem>
  )
}

/**
 * The account button's menu, opened upwards from the foot of the sidebar the
 * way Claude's is. It is rendered inside the drawer on purpose: React counts
 * the portal as the drawer's child, so moving the pointer onto the menu does
 * not read as leaving the sidebar and fold it away under the open menu.
 *
 * The open venue's name heads the menu, and only that one, however many the
 * account owns: switching between them and adding another is done in
 * Settings > Restaurant, where the venue's details are.
 */
function AccountMenu(props: AccountMenuProps) {
  const {
    id,
    anchorEl,
    restaurantName,
    onClose,
    onSettings,
    onHelp,
    onTour,
    onLogout
  } = props
  const { t } = useTranslation()

  const choose = (action: () => void) => () => {
    onClose()
    action()
  }

  // Not an action, so a subheader: MUI skips it when moving focus.
  const restaurantItem = restaurantName && (
    <ListSubheader sx={accountMenuCurrentRestaurantStyles}>
      {/* On a span: MUI's own subheader font would beat a class on the root. */}
      <span className="body-bold-medium">{restaurantName}</span>
    </ListSubheader>
  )

  return (
    <Menu
      id={id}
      anchorEl={anchorEl}
      open={!!anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
      transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      slotProps={{ paper: { sx: accountMenuPaperStyles } }}
    >
      {restaurantItem}
      {/* The venue above, what it lets you do below. */}
      {restaurantItem && <Divider sx={accountMenuDividerStyles} />}
      <AccountMenuItem
        icon="las la-cog"
        label={t('SETTINGS.TITLE')}
        onClick={choose(onSettings)}
      />
      <AccountMenuItem
        icon="las la-question-circle"
        label={t('SIDEBAR.FOOTER.HELP')}
        onClick={choose(onHelp)}
      />
      <AccountMenuItem
        icon="las la-info-circle"
        label={t('SIDEBAR.FOOTER.TOUR')}
        onClick={choose(onTour)}
      />
      <AccountMenuItem
        icon="las la-sign-out-alt"
        label={t('SIDEBAR.FOOTER.LOGOUT')}
        onClick={choose(onLogout)}
        sx={accountMenuLogoutStyles}
      />
    </Menu>
  )
}

export default AccountMenu
